/**
 * CaisseVerticalHub — Navigation Desktop "Top bar Linear-style" (Layout B validé).
 *
 * ⚠️ Malgré son nom historique, ce composant rend une navigation HORIZONTALE
 *    en 2 rangs collée en haut de la page (plus une sidebar verticale).
 *    Nom conservé pour éviter une cascade d'imports dans CaissePage.
 *
 *   ╔═══════════════════════════════════════════════════════════╗
 *   ║  M  Caisse Pro  │  ⋅ Opérations  ⋅ Stock  ⋅ Finances  …  ║ Ligne 1 (familles)
 *   ╟───────────────────────────────────────────────────────────╢
 *   ║  Tables · Prise de cmd · Bons  · Factures · Proforma …   ║ Ligne 2 (items)
 *   ╚═══════════════════════════════════════════════════════════╝
 *
 *   ‣ Aucune surface latérale : 100% de la largeur pour le contenu.
 *   ‣ Recherche ⌘K à droite de la ligne 1.
 *   ‣ Auto-switch de la famille active selon l'onglet actif.
 *   ‣ Persistance de la famille sélectionnée (localStorage).
 *
 * API préservée (props identiques à la version précédente).
 * onCollapseChange(false) est appelé au montage → CaissePage n'applique aucune
 * marge latérale.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Sunrise, LayoutGrid, Printer, Calculator, FileText, Building2, CalendarDays,
  ShoppingCart, ClipboardList, ClipboardCheck, Truck, BookOpen,
  Activity, BarChart3, Receipt, Wallet, Coins, Package, Users,
  RefreshCw, MessageSquare, Settings, KeyRound, Sparkles, Globe,
  Search, Command, ChevronLeft, ChevronRight,
  Briefcase, Boxes, TrendingUp, BookMarked, ChefHat, ShieldCheck,
  UserCircle, Bell,
} from "lucide-react";

// ─── Familles + items (même schéma que Layout A) ─────────────────────────
const FAMILIES = [
  {
    key: "operations", label: "Opérations", icon: Briefcase, hex: "#f59e0b",
    items: [
      { value: "tables", label: "Tables", icon: LayoutGrid, hex: "#0d9488", roles: ["manager", "admin"] },
      { value: "commande", label: "Prise de commandes", icon: Calculator, hex: "#f59e0b", roles: ["server", "manager", "admin"] },
      { value: "bons", label: "Bons", icon: Printer, hex: "#f97316", badge: "bons" },
      { value: "invoices", label: "Factures", icon: FileText, hex: "#3b82f6" },
      { value: "proforma", label: "Proforma", icon: FileText, hex: "#2563eb", roles: ["manager", "admin"] },
      { value: "locations", label: "Locations", icon: Building2, hex: "#a855f7", roles: ["manager", "admin"] },
      { value: "planning", label: "Planning hebdo", icon: CalendarDays, hex: "#3b82f6", roles: ["manager", "admin"], testid: "tab-planning" },
    ],
  },
  {
    key: "stock", label: "Stock & Appro", icon: Boxes, hex: "#0ea5e9",
    items: [
      { value: "achats", label: "Achats", icon: ShoppingCart, hex: "#a855f7", badge: "achats", roles: ["admin"] },
      { value: "courses", label: "Appro Manager", icon: ShoppingCart, hex: "#f59e0b", roles: ["admin"], testid: "tab-appro-manager" },
      { value: "needs", label: "Besoins", icon: ClipboardList, hex: "#6366f1", badge: "needs", roles: ["manager", "admin"], testid: "tab-needs" },
      { value: "valid_demands", label: "Demandes valides", icon: ClipboardCheck, hex: "#10b981", roles: ["manager", "admin"], testid: "tab-valid-demands" },
      { value: "field_stock", label: "Point de stock", icon: ClipboardCheck, hex: "#10b981", badge: "field_stock", roles: ["manager", "admin"], testid: "tab-field-stock" },
    ],
  },
  {
    key: "finances", label: "Finances", icon: TrendingUp, hex: "#10b981",
    items: [
      { value: "journee", label: "Journée", icon: Sunrise, hex: "#f59e0b", roles: ["admin", "manager"], testid: "tab-journee" },
      { value: "point_caisse", label: "Point de la Caisse", icon: Receipt, hex: "#10b981", roles: ["admin", "manager"], testid: "tab-point-caisse" },
      { value: "stats", label: "Statistiques", icon: BarChart3, hex: "#22c55e", badge: "stats", roles: ["admin"] },
      { value: "forecasts", label: "Journal", icon: BookOpen, hex: "#a855f7", roles: ["admin"], testid: "tab-forecasts" },
      { value: "hebdo", label: "Faire le point", icon: BarChart3, hex: "#06b6d4", roles: ["manager", "admin"], testid: "caisse-tab-hebdo" },
      { value: "current-accounts", label: "Compte courant", icon: Wallet, hex: "#059669", roles: ["admin"], testid: "tab-current-accounts" },
      { value: "salaries", label: "Salaires", icon: Wallet, hex: "#10b981", roles: ["admin"], testid: "tab-salaries" },
      { value: "tips", label: "Pourboires", icon: Coins, hex: "#eab308", badge: "tips", testid: "tab-tips" },
    ],
  },
  {
    key: "catalog", label: "Catalogue", icon: BookMarked, hex: "#ec4899",
    items: [
      { value: "products", label: "Produits", icon: Package, hex: "#a855f7" },
      { value: "clients", label: "Clients", icon: Users, hex: "#ec4899" },
      { value: "subscriptions", label: "Abonnements", icon: RefreshCw, hex: "#06b6d4", roles: ["manager", "admin"] },
      { value: "instructions", label: "Notes", icon: MessageSquare, hex: "#14b8a6", badge: "notes", roles: ["manager", "admin"] },
    ],
  },
  {
    key: "notifications", label: "Notifications", icon: Bell, hex: "#f59e0b",
    items: [
      { value: "notifications", label: "Toutes les notifications", icon: Bell, hex: "#f59e0b", roles: ["manager", "admin"], testid: "tab-notifications" },
    ],
  },
  {
    key: "admin", label: "Administration", icon: ShieldCheck, hex: "#ef4444",
    items: [
      { value: "users", label: "Utilisateurs", icon: Settings, hex: "#ef4444", roles: ["admin"] },
      { value: "guest-codes", label: "Codes Invité", icon: KeyRound, hex: "#f59e0b", roles: ["admin"], testid: "tab-guest-codes" },
      { value: "audit", label: "Audit", icon: ClipboardList, hex: "#f43f5e", roles: ["admin"], testid: "tab-audit" },
      { value: "customer-reviews", label: "Avis clients", icon: MessageSquare, hex: "#ec4899", badge: "reviews", roles: ["admin"], testid: "tab-customer-reviews" },
      { value: "site-notifications", label: "Notifs Site", icon: Globe, hex: "#10b981", roles: ["admin"], testid: "tab-site-notifications" },
    ],
  },
  {
    key: "me", label: "Mon espace", icon: UserCircle, hex: "#6366f1",
    items: [
      { value: "mon_point", label: "Mon Point", icon: ClipboardList, hex: "#6366f1", roles: ["server"] },
      { value: "points_agents", label: "Points Agents", icon: ClipboardList, hex: "#6366f1", roles: ["manager"] },
    ],
  },
];

const visible = (item, role) => {
  if (item.hideRoles?.includes(role)) return false;
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.includes(role);
};

const LS_FAMILY = "caisse_topbar_family_v1";

export default function CaisseVerticalHub({
  activeTab,
  setActiveTab,
  currentUser,
  badges = {},
  unreadNotesCount = 0,
  fieldStockPendingCount = 0,
  cuisineNeedsPending = { pending: 0, urgent: 0 },
  onOpenPalette,
  onCollapseChange,
}) {
  const role = currentUser?.role || "server";

  // Le "collapsed" n'a plus de sens en top-bar : on force false pour que le
  // CaissePage n'applique aucune marge latérale.
  useEffect(() => {
    onCollapseChange?.(false);
  }, []);

  const resolveBadge = () => 0;

  const visibleFamilies = useMemo(
    () => FAMILIES
      .map((f) => ({ ...f, items: f.items.filter((i) => visible(i, role)) }))
      .filter((f) => f.items.length > 0),
    [role]
  );

  const familyContaining = (tabValue) =>
    visibleFamilies.find((f) => f.items.some((i) => i.value === tabValue))?.key;

  const [selectedFamily, setSelectedFamilyState] = useState(() => {
    const saved = (typeof window !== "undefined" && (() => {
      try { return localStorage.getItem(LS_FAMILY); } catch { return null; }
    })()) || null;
    return saved || familyContaining(activeTab) || visibleFamilies[0]?.key || "operations";
  });

  const setSelectedFamily = (k) => {
    setSelectedFamilyState(k);
    try { localStorage.setItem(LS_FAMILY, k); } catch { /* ignore */ }
  };

  // Auto-switch famille quand activeTab change ailleurs (palette, breadcrumb, etc.)
  useEffect(() => {
    const fam = familyContaining(activeTab);
    if (fam && fam !== selectedFamily) setSelectedFamily(fam);
  }, [activeTab]);

  const currentFamily = visibleFamilies.find((f) => f.key === selectedFamily) || visibleFamilies[0];

  const familyTotalBadge = (family) =>
    family.items.reduce((sum, it) => sum + (it.badge ? resolveBadge(it.badge) : 0), 0);

  // Scroll horizontal du sous-menu items (avec chevrons)
  const itemsRowRef = useRef(null);
  const scrollItems = (dir) => {
    const el = itemsRowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 220, behavior: "smooth" });
  };

  return (
    <div
      className="ctb-root hidden md:block sticky top-0 z-40 bg-slate-950/95 backdrop-blur-md border-b border-slate-800/80"
      data-testid="admin-sidebar"
    >
      <style>{`
        .ctb-root, .ctb-root * { font-family: 'Space Grotesk', 'Outfit', system-ui, sans-serif; }

        /* Ligne 1 — familles en pills */
        .ctb-family {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 11px;
          padding: 11px 18px;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          color: rgb(148, 163, 184);
          border: 1px solid transparent;
          transition: background-color 140ms ease, color 140ms ease, border-color 140ms ease;
          white-space: nowrap;
        }
        .ctb-family:hover {
          background: rgba(51, 65, 85, 0.5);
          color: rgb(226, 232, 240);
        }
        .ctb-family[data-active="true"] {
          background: color-mix(in srgb, var(--famhex) 12%, transparent);
          color: var(--famhex);
          border-color: color-mix(in srgb, var(--famhex) 30%, transparent);
        }
        .ctb-family-badge {
          min-width: 18px; height: 18px;
          padding: 0 5px;
          margin-left: 4px;
          border-radius: 5px;
          background: rgb(239, 68, 68);
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          font-weight: 700;
          line-height: 18px;
          text-align: center;
        }

        /* Ligne 2 — items en onglets sous-lignés */
        .ctb-items-wrap {
          position: relative;
        }
        .ctb-items {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
          overflow-x: auto;
          scrollbar-width: none;
          scroll-behavior: smooth;
        }
        .ctb-items::-webkit-scrollbar { display: none; }
        .ctb-item {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 13px;
          padding: 13px 18px 15px;
          font-size: 15px;
          font-weight: 600;
          color: rgb(148, 163, 184);
          border-bottom: 3px solid transparent;
          transition: color 140ms ease, background-color 140ms ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ctb-item:hover {
          color: white;
          background: rgba(51, 65, 85, 0.3);
        }
        .ctb-item[data-active="true"] {
          color: white;
          border-bottom-color: var(--ithex);
          background: rgba(51, 65, 85, 0.4);
        }
        .ctb-item-badge {
          min-width: 18px; height: 18px;
          padding: 0 5px;
          border-radius: 5px;
          background: rgb(239, 68, 68);
          color: white;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          font-weight: 700;
          line-height: 18px;
          text-align: center;
        }
        .ctb-scroll-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 26px; height: 26px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          background: rgb(15, 23, 42);
          border: 1px solid rgb(51, 65, 85);
          color: rgb(148, 163, 184);
          transition: color 140ms;
          z-index: 2;
        }
        .ctb-scroll-btn:hover { color: white; }
        .ctb-scroll-left { left: 8px; }
        .ctb-scroll-right { right: 8px; }

        .ctb-kbd {
          font-family: 'JetBrains Mono', monospace;
          background: rgba(51,65,85,0.6);
          box-shadow: 0 1px 0 rgba(255,255,255,0.05) inset;
        }
      `}</style>

      {/* ────────── Ligne 1 : logo + familles + recherche ────────── */}
      <div className="flex items-center gap-2 px-4 lg:px-6 h-16 border-b border-slate-800/60">
        {/* Logo + nom */}
        <div className="flex items-center gap-2.5 flex-shrink-0 pr-3 border-r border-slate-800/60 mr-1">
          <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
            <span className="text-slate-900 font-black text-[15px]">M</span>
          </div>
          <div className="hidden lg:block leading-tight">
            <p className="text-white font-black text-[13.5px] tracking-tight">Caisse Pro</p>
            <p className="text-slate-500 text-[9.5px] uppercase tracking-[0.15em] font-bold">
              {currentUser?.role === "admin" ? "Administrateur" : (currentUser?.full_name || currentUser?.role || "—")}
            </p>
          </div>
        </div>

        {/* Familles en pills — centrées */}
        <nav className="flex items-center justify-center gap-1.5 flex-1 overflow-x-auto scrollbar-none">
          {visibleFamilies.map((f) => {
            const Icon = f.icon;
            const isSel = selectedFamily === f.key;
            const total = familyTotalBadge(f);
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setSelectedFamily(f.key)}
                data-active={isSel}
                className="ctb-family"
                style={{ "--famhex": f.hex }}
                data-testid={`topbar-family-${f.key}`}
              >
                <Icon className="w-[24px] h-[24px]" strokeWidth={2.2} />
                <span>{f.label}</span>
                {total > 0 && (
                  <span className="ctb-family-badge">{total > 99 ? "99+" : total}</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bouton recherche */}
        <button
          type="button"
          onClick={onOpenPalette}
          className="flex-shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-900/70 hover:bg-slate-800 border border-slate-700/60 hover:border-slate-600 text-slate-400 hover:text-white text-[12px] transition"
          data-testid="sidebar-search-btn"
        >
          <Search className="w-3.5 h-3.5" strokeWidth={2.4} />
          <span className="hidden lg:inline">Rechercher…</span>
          <kbd className="ctb-kbd inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9.5px] rounded text-slate-300">
            <Command className="w-2.5 h-2.5" /> K
          </kbd>
        </button>
      </div>

      {/* ────────── Ligne 2 : items de la famille active ────────── */}
      <div className="ctb-items-wrap relative">
        {/* Boutons scroll (visibles si contenu déborde) */}
        <button
          type="button"
          className="ctb-scroll-btn ctb-scroll-left hidden md:flex lg:hidden"
          onClick={() => scrollItems(-1)}
          aria-label="Défiler à gauche"
        >
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2.4} />
        </button>

        <div
          ref={itemsRowRef}
          className="ctb-items px-4 lg:px-6"
        >
          {currentFamily?.items.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.value;
            const bVal = item.badge ? resolveBadge(item.badge) : 0;
            return (
              <button
                key={item.value}
                type="button"
                onClick={() => setActiveTab(item.value)}
                data-active={isActive}
                className="ctb-item"
                style={{ "--ithex": item.hex }}
                data-testid={item.testid || `sidebar-item-${item.value}`}
              >
                <Icon
                  className="w-[24px] h-[24px]"
                  strokeWidth={2.2}
                  style={{ color: item.hex }}
                />
                <span>{item.label}</span>
                {bVal > 0 && (
                  <span className={`ctb-item-badge ${item.badge === "cuisine_needs" && cuisineNeedsPending.urgent > 0 ? "animate-pulse" : ""}`}>
                    {bVal > 99 ? "99+" : bVal}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="ctb-scroll-btn ctb-scroll-right hidden md:flex lg:hidden"
          onClick={() => scrollItems(1)}
          aria-label="Défiler à droite"
        >
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
